import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Image as PosterIcon, Plus, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { AuthedImage, fmtDate } from "@/components/posters/posterUtils";

function TemplateCard({ t }) {
  return (
    <Link to={`/portal/posters/new/${t.id}`} data-testid={`poster-template-${t.id}`}
      className="group block bg-white border border-stone-200 rounded-xl overflow-hidden hover:border-stone-950 hover:shadow-md transition-[border-color,box-shadow]">
      <AuthedImage path={`/portal/posters/templates/${t.id}/thumbnail.jpg`} alt={t.title} className="w-full aspect-[595/842] object-cover" />
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-stone-900 truncate">{t.title}</div>
          {t.description && <div className="text-[11px] text-stone-500 truncate">{t.description}</div>}
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-stone-500 group-hover:text-stone-950 transition-colors"><Plus className="w-3 h-3" /> Create</span>
      </div>
    </Link>
  );
}

function SavedRow({ p }) {
  return (
    <Link to={`/portal/posters/${p.id}`} data-testid={`poster-saved-${p.id}`}
      className="flex items-center gap-4 bg-white border border-stone-200 rounded-xl p-3 hover:border-stone-950 transition-colors">
      <AuthedImage path={`/portal/posters/${p.id}/thumbnail.jpg`} bust={p.updated_at} alt={p.title} className="w-14 h-20 object-cover rounded border border-stone-100 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-stone-900 truncate" data-testid={`poster-saved-title-${p.id}`}>{p.title}</div>
        <div className="text-[11px] text-stone-500">{p.template_title || "Template"}{p.template_status && p.template_status !== "published" ? " (older version)" : ""}</div>
        <div className="text-[11px] text-stone-400">Updated {fmtDate(p.updated_at)}</div>
      </div>
    </Link>
  );
}

export default function PortalPostersPage() {
  const [templates, setTemplates] = useState(null);
  const [posters, setPosters] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([api.get("/portal/posters/templates"), api.get("/portal/posters")])
      .then(([t, p]) => { setTemplates(t.data.items || []); setPosters(p.data.items || []); })
      .catch((e) => setError(e?.response?.data?.detail || "Poster Studio could not be loaded. Please try again."));
  }, []);
  return (
    <div className="px-6 md:px-10 py-8 max-w-6xl mx-auto" data-testid="portal-posters-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 flex items-center gap-1.5"><PosterIcon className="w-3 h-3" /> Marketing materials</div>
        <h1 className="font-display text-4xl font-black text-stone-950 mt-1">Poster Studio</h1>
        <p className="text-sm text-stone-600 mt-1">Personalise an HQ poster with your franchise details, then download it as a print-ready PDF or a JPG for social media.</p>
      </div>
      {error && <div className="text-sm text-red-700 mb-6" data-testid="posters-error">{error}</div>}
      {!error && (templates == null || posters == null) && <Loader2 className="w-5 h-5 animate-spin text-stone-400" />}
      {templates && (
        <section className="mb-10" data-testid="poster-templates">
          <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-3">Start a new poster</h2>
          {templates.length === 0
            ? <p className="text-sm text-stone-500">No poster templates are available yet.</p>
            : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{templates.map((t) => <TemplateCard key={t.id} t={t} />)}</div>}
        </section>
      )}
      {posters && (
        <section data-testid="poster-saved-list">
          <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-3">Your saved posters</h2>
          {posters.length === 0
            ? <p className="text-sm text-stone-500" data-testid="poster-saved-empty">You haven&apos;t saved any posters yet.</p>
            : <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{posters.map((p) => <SavedRow key={p.id} p={p} />)}</div>}
        </section>
      )}
    </div>
  );
}
